import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { collection, doc, getDoc, getDocs, orderBy, query } from "firebase/firestore";
import { LineChart } from "@mui/x-charts/LineChart";
import { db } from "../../firebase";
import "./css/ContadorStats.css";
import { Box, Grid, Card, CardContent, Typography,Divider } from "@mui/material";
import MapIcon from "@mui/icons-material/Map";
import StatCard from "./StatCard";
import { useDispatch } from "react-redux";
import { hideLoading, showLoading } from "../../../redux/actions/loadingActions";
import { BarChart } from "@mui/x-charts/BarChart";

type Counter = {
    value: number;
};

type CounterLog = {
    createdAt: any;
    increment: number;
    totalAfter: number;
};

const HeatmapCard = ({ onClick }: { onClick: () => void }) => (
    <Card
        className="heatmap-nav-card"
        onClick={onClick}
        elevation={0}
        sx={{
            height: "100%",
            cursor: "pointer",
            borderRadius: 2,
            border: "1px solid",
            borderColor: "#40c463",
            background:
                "linear-gradient(135deg, rgba(25,118,210,0.08), rgba(25,118,210,0.02))",
            boxShadow: "0 6px 20px rgba(25,118,210,0.15)",
            transition: "all .25s ease",
            "&:hover": {
                boxShadow: "0 12px 32px rgba(25,118,210,0.3)",
                transform: "translateY(-3px)",
                background:
                    "linear-gradient(135deg, rgba(25,118,210,0.15), rgba(25,118,210,0.04))",
            },
        }}
    >
        <CardContent
            sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
            }}
        >
            <Box
                sx={{
                    width: 56,
                    height: 56,
                    borderRadius: "50%",
                    backgroundColor: "#40c463",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    mb: 1.5,
                    boxShadow: "0 6px 16px rgba(25,118,210,0.4)",
                }}
            >
                <MapIcon sx={{ color: "#fff", fontSize: 28 }} />
            </Box>

            <Typography variant="subtitle1" fontWeight={700}>
                Mapa de calor
            </Typography>
        </CardContent>
    </Card>
);

const METRIC_INFO: Record<string, string> = {
    total: "Suma total acumulada desde el primer registro.",
    mediaDiaria: "Promedio diario calculado sobre los días con actividad.",
    mejorDia: "Mayor cantidad registrada en un solo día.",
    diasActivos: "Número de días en los que hubo al menos una acción.",
    rachaActual: "Días consecutivos con actividad hasta hoy.",
    mejorRacha: "Mayor número de días consecutivos con actividad.",
    peorRacha: "Mayor número de días consecutivos sin actividad.",
    estaSemana: "Total acumulado desde el lunes de la semana actual.",
    esteMes: "Total acumulado desde el inicio del mes actual.",
};

const ContadorStats: React.FC = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();

    const [counter, setCounter] = useState<Counter | null>(null);
    const [logs, setLogs] = useState<CounterLog[]>([]);

    useEffect(() => {
        dispatch(showLoading());

        const fetchData = async () => {
            const counterSnap = await getDoc(doc(db, "counter", "main"));
            const logsSnap = await getDocs(
                query(
                    collection(db, "counter_logs"),
                    orderBy("createdAt", "asc"),
                ),
            );

            setCounter(counterSnap.data() as Counter);
            setLogs(logsSnap.docs.map((d) => d.data() as CounterLog));
            dispatch(hideLoading());
        };

        fetchData();
    }, [dispatch]);

    if (!counter) return null;

    /* =======================
     NORMALIZACIÓN
    ======================= */

    const dailyMap = logs.reduce<Record<string, number>>((acc, log) => {
        const d = log.createdAt.toDate();
        d.setHours(0, 0, 0, 0); // normalización LOCAL
        const key = d.getTime(); // timestamp del día

        acc[key] = (acc[key] || 0) + log.increment;
        return acc;
    }, {});

    const days = Object.keys(dailyMap)
        .map(Number)
        .sort((a, b) => a - b);

    /* =======================
     STATS
    ======================= */

    const total = Object.values(dailyMap).reduce((a, b) => a + b, 0);
    const activeDays = days.length;
    const bestDay = Math.max(...Object.values(dailyMap), 0);
    const dailyAverage = activeDays ? total / activeDays : 0;

    /* =======================
     RACHAS
    ======================= */

    const getCurrentStreak = () => {
        if (days.length === 0) return 0;

        let streak = 0;
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let cursor = today.getTime();

        const set = new Set(days);

        while (set.has(cursor)) {
            streak++;
            cursor -= 86400000;
        }

        return streak;
    };

    const getBestStreak = () => {
        let best = 1;
        let current = 1;

        for (let i = 1; i < days.length; i++) {
            const diff = (days[i] - days[i - 1]) / 86400000;

            if (diff === 1) {
                current++;
            } else {
                best = Math.max(best, current);
                current = 1;
            }
        }

        return Math.max(best, current);
    };

    const getWorstStreak = () => {
        let worst = 0;

        for (let i = 1; i < days.length; i++) {
            const prev = new Date(days[i - 1]);
            const current = new Date(days[i]);

            const prevDayOnly = Date.UTC(
                prev.getFullYear(),
                prev.getMonth(),
                prev.getDate()
            );

            const currentDayOnly = Date.UTC(
                current.getFullYear(),
                current.getMonth(),
                current.getDate()
            );

            const gap = (currentDayOnly - prevDayOnly) / 86400000 - 1;

            worst = Math.max(worst, gap);
        }

        return worst;
    };

    const getWeekStart = (date: Date) => {
        const d = new Date(date);
        const day = d.getDay(); // 0 (domingo) - 6 (sábado)
        const diff = day === 0 ? -6 : 1 - day; // lunes como inicio
        d.setDate(d.getDate() + diff);
        d.setHours(0, 0, 0, 0);
        return d;
    };

    const now = new Date();
    const weekStart = getWeekStart(now);
    const currentYear = now.getFullYear();

    const weeklyTotal = logs.reduce((acc, log) => {
        const logDate = log.createdAt.toDate();
        if (logDate >= weekStart) {
            return acc + log.increment;
        }
        return acc;
    }, 0);

    const monthlyTotal = logs.reduce((acc, log) => {
        const d = log.createdAt.toDate();
        if (
            d.getMonth() === now.getMonth() &&
            d.getFullYear() === now.getFullYear()
        ) {
            return acc + log.increment;
        }
        return acc;
    }, 0);

    // Inicializamos los 12 meses a 0
    const monthlyMap: Record<number, number> = {};
    for (let m = 0; m < 12; m++) {
        monthlyMap[m] = 0;
    }

    // Sumamos los logs del año actual
    logs.forEach(log => {
        const d = log.createdAt.toDate();
        if (d.getFullYear() === currentYear) {
            monthlyMap[d.getMonth()] += log.increment;
        }
    });

    const monthlyEntries = Array.from({ length: 12 }, (_, month) => ({
        label: new Date(currentYear, month).toLocaleString("es-ES", {
            month: "short",
        }),
        value: monthlyMap[month],
        month,
    }));

    const activeDaysThisYear = Object.keys(dailyMap)
        .map(Number)
        .filter(timestamp => {
            const d = new Date(timestamp);
            return d.getFullYear() === currentYear;
        });

    const totalThisYear = activeDaysThisYear.reduce(
        (acc, timestamp) => acc + dailyMap[timestamp],
        0
    );

    const monthsWithActivity = new Set(
        activeDaysThisYear.map(timestamp => {
            const d = new Date(timestamp);
            return d.getMonth();
        })
    ).size;

    const monthlyAverage = monthsWithActivity > 0 ? totalThisYear / monthsWithActivity : 0;

    /* =======================
     GRÁFICA
    ======================= */

    const chartData = logs.map((log) => ({
        x: log.createdAt.toDate(),
        y: log.totalAfter,
    }));

    return (
        <div className="container-fluid">
            <div className="text-center">
                <h2 className="pb-3 page-heading">Estadísticas del amor</h2>
            </div>

            <div className="container-fluid content pb-3">
                <Grid container spacing={2}>
                    <Grid item xs={6} md={6}>
                        <Card
                            className="total-card"
                            elevation={0}
                            sx={{
                                height: "100%",
                                borderRadius: 2,
                                border: "1px solid",
                                borderColor: "divider",
                                boxShadow: "0 8px 28px rgba(0,0,0,0.12)",
                            }}
                        >
                            <CardContent>
                                <Typography
                                    variant="overline"
                                    color="text.secondary"
                                >
                                    Total
                                </Typography>
                                <Typography
                                    variant="h3"
                                    sx={{ fontWeight: 700, mt: 1 }}
                                >
                                    {total}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={6} md={6}>
                        <HeatmapCard
                            onClick={() => navigate("/contador/mapa-de-calor")}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <StatCard
                            label="Media diaria"
                            value={dailyAverage.toFixed(1)}
                            tooltip={METRIC_INFO.mediaDiaria}
                        />
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <StatCard
                            label="Mejor día"
                            value={bestDay}
                            tooltip={METRIC_INFO.mejorDia}
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" className="section-subtitle" sx={{ mt: 5, mb: 2 }}>
                    Actividad
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            label="Días activos"
                            value={activeDays}
                            tooltip={METRIC_INFO.diasActivos}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            label="Racha actual"
                            value={`${getCurrentStreak()} días`}
                            tooltip={METRIC_INFO.rachaActual}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            label="Mejor racha"
                            value={`${getBestStreak()} días`}
                            tooltip={METRIC_INFO.mejorRacha}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            label="Peor racha"
                            value={`${getWorstStreak()} días`}
                            tooltip={METRIC_INFO.peorRacha}
                        />
                    </Grid>
                </Grid>

                <Typography variant="h6" className="section-subtitle" sx={{ mt: 5, mb: 2 }}>
                    Periodo actual
                </Typography>

                <Grid container spacing={2}>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            label="Esta semana"
                            value={weeklyTotal}
                            tooltip={METRIC_INFO.estaSemana}
                        />
                    </Grid>
                    <Grid item xs={6} md={3}>
                        <StatCard
                            label="Este mes"
                            value={monthlyTotal}
                            tooltip={METRIC_INFO.esteMes}
                        />
                    </Grid>
                </Grid>

                <Divider className="section-divider" sx={{ my: 4 }} />

                {/* GRÁFICA */}
                <Typography variant="h6" className="section-subtitle" gutterBottom>
                    Evolución acumulada
                </Typography>

                <div className="chart-container">
                    <LineChart
                        xAxis={[{ dataKey: "x", scaleType: "time" }]}
                        series={[{ dataKey: "y", label: "Total" }]}
                        dataset={chartData}
                        height={300}
                    />
                </div>

                <Divider className="section-divider" sx={{ my: 3 }} />

                <Typography variant="h6" className="section-subtitle" gutterBottom>
                    Actividad por mes
                </Typography>

                <div className="chart-container">
                    <BarChart
                        xAxis={[
                            {
                                data: monthlyEntries.map(m => m.label),
                                scaleType: "band",
                                tickLabelInterval: () => true,
                                tickLabelStyle: {
                                    angle: -30,
                                    textAnchor: "end",
                                    fontSize: 11,
                                },
                                tickMinStep: 1,
                            },
                        ]}
                        series={[
                            {
                                data: monthlyEntries.map(m => m.value),
                                label: `Total mensual (Media: ${monthlyAverage.toFixed(2)})`,
                            },
                        ]}
                        height={320}
                    />
                </div>
            </div>
        </div>
    );
};

export default ContadorStats;
